import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { notifyMemorialFollowers } from '../../../hooks/useTributeNotifications';
import { writeFetch, getSession } from '../../../api';

const { FiX, FiLock, FiSend, FiCheck, FiSmile, FiTrash2, FiSearch, FiSettings, FiClock, FiShield } = FiIcons;

const GIFTS = [
  { id: 'candle', label: 'Light a Candle', emoji: '🕯️', color: 'text-amber-500', bg: 'bg-amber-50',
    options: [{ id: 'white', label: 'Pure White', color: '#FFFFFF' }, { id: 'gold', label: 'Eternal Gold', color: '#C5A880' }, { id: 'blue', label: 'Serene Blue', color: '#60A5FA' }]
  },
  { id: 'flower', label: 'Leave Flowers', emoji: '🌹', color: 'text-rose-500', bg: 'bg-rose-50',
    options: [{ id: 'rose', label: 'Red Rose' }, { id: 'lily', label: 'White Lily' }, { id: 'sunflower', label: 'Sunflower' }]
  },
  { id: 'dove', label: 'Peace Dove', emoji: '🕊️', color: 'text-blue-400', bg: 'bg-blue-50',
    options: [{ id: 'single', label: 'Single Dove' }, { id: 'pair', label: 'Pair of Doves' }]
  },
  { id: 'prayer', label: 'Offer Prayer', emoji: '🙏', color: 'text-accent-gold', bg: 'bg-brand-light',
    options: [{ id: 'peace', label: 'Prayer for Peace' }, { id: 'strength', label: 'Prayer for Strength' }, { id: 'gratitude', label: 'Word of Gratitude' }]
  },
];

const PRESET_GIFS = [
  { id: 1, url: 'https://media.giphy.com/media/3o7TKsWpP70Y0q9B4A/giphy.gif', tag: 'Candle' },
  { id: 2, url: 'https://media.giphy.com/media/l41lI4bWfQ0aFqY6o/giphy.gif', tag: 'Dove' },
  { id: 3, url: 'https://media.giphy.com/media/xT0xeunmS9r3tP8Y6I/giphy.gif', tag: 'Sunset' },
  { id: 4, url: 'https://media.giphy.com/media/3o7TKVUn7iM8FMEU24/giphy.gif', tag: 'Flowers' },
];

const getTributeApprovalSetting = (memorialId) => {
  const stored = localStorage.getItem(`tribute_approval_${memorialId}`);
  if (stored !== null) return stored === '1';
  return true;
};

const SignGuestbookModal = ({ isOpen, onClose, memorialName, memorialId, onSumbit }) => {
  const [formData, setFormData] = useState({
    name: '', relation: '', message: '',
    gift: 'candle', giftOption: 'white',
    isPrivate: false, gifUrl: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);

  const selectedGift = GIFTS.find(g => g.id === formData.gift);
  const requiresApproval = getTributeApprovalSetting(memorialId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const user = getSession();
    const isAutoApproved = !requiresApproval;

    const tribute = {
      ...formData,
      id: Date.now(),
      date: new Date().toISOString(),
      author: formData.name,
      type: formData.gift,
      pendingApproval: requiresApproval && !formData.isPrivate,
    };

    // Save to condolences table
    writeFetch('create/condolences', {
      method: 'POST',
      body: JSON.stringify({
        user_id: user?.id || 'guest',
        memorial_page_id: memorialId || 0,
        author_name: formData.name,
        author_email: user?.email || '',
        message: formData.message,
        is_approved: isAutoApproved ? 1 : 0,
        is_public: formData.isPrivate ? 0 : 1,
        created_at: new Date().toISOString(),
      })
    }).catch(() => {});

    if (!formData.isPrivate && memorialId) {
      if (formData.gift === 'candle') {
        writeFetch('create/candle_tributes', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user?.id || 'guest',
            memorial_page_id: memorialId,
            lit_by_name: formData.name,
            message: formData.message,
            candle_type: formData.giftOption,
            lit_at: new Date().toISOString(),
          })
        }).catch(() => {});
      } else if (formData.gift === 'flower') {
        writeFetch('create/flower_tributes', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user?.id || 'guest',
            memorial_page_id: memorialId,
            sent_by_name: formData.name,
            message: formData.message,
            flower_type: formData.giftOption,
            sent_at: new Date().toISOString(),
          })
        }).catch(() => {});
      }

      if (isAutoApproved) {
        await notifyMemorialFollowers({
          memorialPageId: memorialId,
          memorialName,
          actorName: formData.name || 'Someone',
          tributeType: formData.gift,
          giftType: formData.giftOption,
        });
        window.dispatchEvent(new CustomEvent('memorial_activity', {
          detail: { type: formData.gift, author: formData.name, memorialId, timestamp: new Date().toISOString() }
        }));
      } else {
        // ── KEY: dispatch event so ModerationQueue and inline panel both receive it
        window.dispatchEvent(new CustomEvent('pending_tribute', {
          detail: {
            author: formData.name,
            relation: formData.relation,
            memorialName,
            memorialId,
            message: formData.message,
            tributeType: formData.gift,
            giftOption: formData.giftOption,
            submittedAt: new Date().toISOString(),
          }
        }));
      }
    }

    setIsSubmitting(false);

    if (requiresApproval && !formData.isPrivate) {
      setIsPending(true);
      setTimeout(() => {
        setIsPending(false);
        onClose();
        setFormData({ name: '', relation: '', message: '', gift: 'candle', giftOption: 'white', isPrivate: false, gifUrl: null });
      }, 3000);
    } else {
      setIsSuccess(true);
      setTimeout(() => {
        onSumbit(tribute);
        setIsSuccess(false);
        onClose();
        setFormData({ name: '', relation: '', message: '', gift: 'candle', giftOption: 'white', isPrivate: false, gifUrl: null });
      }, 1500);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="relative p-8 md:p-12 overflow-y-auto">
          <button onClick={onClose} className="absolute top-8 right-8 p-2 hover:bg-brand-light rounded-full transition-colors z-10">
            <SafeIcon icon={FiX} className="w-6 h-6 text-text-muted" />
          </button>

          <AnimatePresence>
            {isPending && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 bg-white rounded-[40px] flex flex-col items-center justify-center text-center p-12 z-20"
              >
                <div className="w-20 h-20 bg-accent-gold/10 text-accent-gold rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiClock} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-serif text-accent-navy mb-3">Tribute Submitted!</h3>
                <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-4">
                  <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
                  Pending Approval
                </div>
                <p className="text-sm text-text-muted font-light leading-relaxed max-w-xs">
                  Your tribute is awaiting review by the memorial owner before it appears publicly. Thank you for your kindness.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mb-8">
            <h2 className="text-3xl font-serif text-accent-navy mb-2">Sign the Guestbook</h2>
            <p className="text-text-muted font-light">Share a memory or message for {memorialName}'s family.</p>
            {requiresApproval && (
              <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2.5 rounded-2xl text-xs font-bold">
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5 flex-shrink-0" />
                Tributes on this memorial are reviewed before publishing.
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Your Full Name</label>
                <input required type="text"
                  className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none transition-all font-medium"
                  placeholder="e.g. Sarah Jenkins"
                  value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Relationship</label>
                <input type="text"
                  className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none transition-all font-medium"
                  placeholder="e.g. Childhood Friend"
                  value={formData.relation} onChange={e => setFormData({ ...formData, relation: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-6">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Select & Customize a Virtual Gift</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {GIFTS.map(gift => (
                  <button key={gift.id} type="button"
                    onClick={() => setFormData({ ...formData, gift: gift.id, giftOption: gift.options[0].id })}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 group ${formData.gift === gift.id ? 'border-accent-gold bg-brand-light' : 'border-brand-dark hover:border-accent-gold/30'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gift.bg} ${gift.color} text-xl`}>{gift.emoji}</div>
                    <span className="text-[10px] font-black uppercase tracking-tighter text-text-muted group-hover:text-accent-navy">{gift.label}</span>
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={formData.gift} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="bg-brand-light p-6 rounded-3xl border border-brand-dark overflow-hidden"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <SafeIcon icon={FiSettings} className="text-accent-gold w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Personalize your {selectedGift?.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {selectedGift?.options.map(option => (
                      <button key={option.id} type="button"
                        onClick={() => setFormData({ ...formData, giftOption: option.id })}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border-2 flex items-center gap-2 ${formData.giftOption === option.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
                      >
                        {option.color && <div className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: option.color }} />}
                        {option.label}
                        {formData.giftOption === option.id && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Your Message</label>
                <button type="button" onClick={() => setShowGifPicker(!showGifPicker)}
                  className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${formData.gifUrl ? 'text-green-600' : 'text-accent-gold hover:text-accent-navy'}`}
                >
                  <SafeIcon icon={FiSmile} />
                  {formData.gifUrl ? 'GIF Attached' : 'Attach a GIF'}
                </button>
              </div>

              {showGifPicker && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  className="bg-brand-light p-4 rounded-2xl border-2 border-brand-dark space-y-4"
                >
                  <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl border border-brand-dark">
                    <SafeIcon icon={FiSearch} className="text-text-muted" />
                    <input type="text" placeholder="Search animated memories..." className="bg-transparent outline-none text-xs w-full" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {PRESET_GIFS.map(gif => (
                      <button key={gif.id} type="button"
                        onClick={() => { setFormData({ ...formData, gifUrl: gif.url }); setShowGifPicker(false); }}
                        className={`aspect-video rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.02] ${formData.gifUrl === gif.url ? 'border-accent-gold' : 'border-transparent'}`}
                      >
                        <img src={gif.url} alt={gif.tag} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {formData.gifUrl && !showGifPicker && (
                <div className="relative w-48 aspect-video rounded-2xl overflow-hidden border-2 border-accent-gold group mt-2">
                  <img src={formData.gifUrl} alt="Selected memory" className="w-full h-full object-cover" />
                  <button onClick={() => setFormData({ ...formData, gifUrl: null })} className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                  </button>
                </div>
              )}

              <textarea required
                className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none transition-all font-light min-h-[160px] resize-none text-lg"
                placeholder="Write your tribute here..."
                value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-4">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.isPrivate ? 'bg-accent-navy border-accent-navy' : 'border-brand-dark group-hover:border-accent-gold'}`}>
                  <input type="checkbox" className="hidden" checked={formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })} />
                  {formData.isPrivate && <SafeIcon icon={FiCheck} className="text-white w-4 h-4" />}
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiLock} className="w-3.5 h-3.5 text-text-muted" />
                  <span className="text-sm text-text-muted">Keep message private for the family</span>
                </div>
              </label>

              <button type="submit" disabled={isSubmitting || isSuccess}
                className="w-full sm:w-auto btn-primary px-12 py-5 text-lg flex items-center justify-center gap-3 min-w-[200px]"
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isSuccess ? (
                  <><SafeIcon icon={FiCheck} /> Posted</>
                ) : requiresApproval && !formData.isPrivate ? (
                  <><SafeIcon icon={FiSend} /> Submit for Review</>
                ) : (
                  <><SafeIcon icon={FiSend} /> Post Tribute</>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default SignGuestbookModal;